import { Injector } from '@angular/core';
import {
  GenericHandleBarModel,
  HandlebarModelService,
  filterIfHandlebarNotRendered,
} from '../services/handlebar-model.service';
import Handlebars from 'handlebars';
import { map, Observable, of } from 'rxjs';

/**
 * Helper function to render options using a pre-built Handlebars model.
 */
function renderOptionsWithModel(
  options: Record<string, string>[],
  labelKey: string,
  valueKey: string,
  model: GenericHandleBarModel,
): Record<string, string>[] {
  return options.map((option) => {
    const template = Handlebars.compile(String(option[labelKey] ?? ''));
    const rendered = filterIfHandlebarNotRendered(template(model)) ?? '';
    return {
      [labelKey]: rendered,
      [valueKey]: option[valueKey],
    };
  });
}

/**
 * Renders option labels using Handlebars templates for a given array of options.
 *
 * For each option, this function:
 *  - Builds a Handlebars model using the option's label value and required services.
 *  - Compiles and renders the label as a Handlebars template with the built model.
 *  - Maps the rendered label to the option's value.
 *
 * Returns an Observable that emits a record mapping each option's value to its rendered label.
 * Useful for dynamically generating select/dropdown options with templated labels.
 *
 * @param options - The array of option objects to render.
 * @param labelKey - The key in each option object to use as the label/template.
 * @param valueKey - The key in each option object to use as the unique identifier.
 * @param injector - Angular Injector for accessing required services.
 * @returns Observable<Record<string, string>> - A map of option values to rendered labels.
 */
export function renderOptionsWithHandlebars(
  options: Record<string, string>[] = [],
  labelKey: string,
  valueKey: string,
  injector: Injector,
): Observable<{
  renderedOptions: Record<string, string>;
  optionsWithRenderedLabelAndKey: Record<string, string>[];
  model: GenericHandleBarModel;
}> {
  if (!options?.length) {
    return of({
      renderedOptions: {},
      optionsWithRenderedLabelAndKey: [],
      model: {} as GenericHandleBarModel,
    });
  }

  const handlebarModelService = injector.get(HandlebarModelService);

  return handlebarModelService.buildModel().pipe(
    map((model) => {
      const optionsWithRenderedLabelAndKey = renderOptionsWithModel(options, labelKey, valueKey, model);
      const renderedOptions = Object.fromEntries(
        optionsWithRenderedLabelAndKey.map((option) => [option[valueKey], option[labelKey]]),
      ) as Record<string, string>;
      return { renderedOptions, optionsWithRenderedLabelAndKey, model };
    }),
  );
}
